import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { authService } from '@/services/auth';
import { useNavigate, Link } from 'react-router-dom';
import { AppleSignInButton } from './AppleSignInButton';
import { GoogleSignInButton } from './GoogleSignInButton';
import { cn, isMobileDevice } from '@/lib/utils';
import { useLegalDocumentModal } from '@/hooks/useLegalDocumentModal';
import { LegalDocumentModal } from '@/components/legal/LegalDocumentModal';

interface RegisterFormData {
  first_name: string;
  last_name?: string;
  email: string;
  password: string;
  invite_code?: string;
  acceptTerms: boolean;
}

export default function RegisterForm() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, documentType, openDocument, closeDocument } = useLegalDocumentModal();
  
  const { register, handleSubmit, formState: { errors }, setError, watch, setValue } = useForm<RegisterFormData>();

  const handleAuthSuccess = () => {
    navigate('/');
  };

  const handleAuthError = (error: string) => {
    console.error('Authentication error:', error);
  };
  
  const acceptTerms = watch('acceptTerms');
  const inviteCode = watch('invite_code');
  const shouldShowGoogle = true; // Enable Google Sign-in on all platforms
  
  const onSubmit = async (data: RegisterFormData) => {
    if (!acceptTerms) {
      setError('acceptTerms', {
        type: 'manual',
        message: 'You must accept the Terms of Service and Privacy Policy to create an account.'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Proceed with registration directly - no email validation
      const result = await authService.signUp(data);
      
      if (result.error) {
        toast.error(result.error.message);
      } else if (result.user) {
        // Redirect to login page with success state
        navigate('/auth/login', { 
          state: { 
            registrationSuccess: true, 
            email: data.email 
          } 
        });
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to register");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Social Sign-Up Buttons */}
      {shouldShowGoogle && (
        <div className="space-y-4">
          {/* Google Sign-Up Button */}
          <GoogleSignInButton 
            onSuccess={handleAuthSuccess}
            onError={handleAuthError}
            inviteCode={inviteCode}
            variant="signup"
          />
          
          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border/30" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-background px-3 text-muted-foreground">
                or continue with email
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">
            First Name
          </Label>
          <Input
            id="first_name"
            placeholder="John"
            disabled={isLoading}
            {...register('first_name', {
              required: 'First name is required'
            })}
          />
          {errors.first_name && (
            <p className="text-sm text-red-500">
              {errors.first_name.message}
            </p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email">
            Email
          </Label>
          <Input
            id="email"
            placeholder="name@example.com"
            type="email"
            autoCapitalize="none"
            autoComplete="email"
            autoCorrect="off"
            disabled={isLoading}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Please enter a valid email'
              }
            })}
          />
          {errors.email && (
            <div className="text-sm text-red-500">
              {errors.email.message}
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            disabled={isLoading}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
          />
          {errors.password && (
            <p className="text-sm text-red-500">
              {errors.password.message}
            </p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="invite_code">
            Family Invite Code (Optional)
          </Label>
          <Input
            id="invite_code"
            placeholder="Enter code if you have one"
            disabled={isLoading}
            {...register('invite_code')}
          />
          <p className="text-xs text-gray-500">
            If you have a family invite code, enter it here to join an existing family.
          </p>
        </div>

        {/* Terms and Privacy Acceptance */}
        <div className="space-y-3">
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 pt-0.5">
              <Checkbox
                id="acceptTerms"
                checked={acceptTerms || false}
                onCheckedChange={(checked) => setValue('acceptTerms', checked as boolean)}
                disabled={isLoading}
              />
            </div>
            <Label 
              htmlFor="acceptTerms" 
              className="text-sm leading-relaxed flex-1"
            >
              I agree to the{' '}
              {isMobileDevice() ? (
                <button
                  type="button"
                  onClick={() => openDocument('terms')}
                  className="text-primary hover:underline underline"
                >
                  Terms of Service
                </button>
              ) : (
                <Link 
                  to="/terms" 
                  className="text-primary hover:underline"
                >
                  Terms of Service
                </Link>
              )}
              {' '}and{' '}
              {isMobileDevice() ? (
                <button
                  type="button"
                  onClick={() => openDocument('privacy')}
                  className="text-primary hover:underline underline"
                >
                  Privacy Policy
                </button>
              ) : (
                <Link 
                  to="/privacy" 
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </Link>
              )}
            </Label>
          </div>
          {errors.acceptTerms && (
            <p className="text-sm text-red-500">
              {errors.acceptTerms.message}
            </p>
          )}
        </div>
        
        <div>
          <Button type="submit" className="w-full" disabled={isLoading || !acceptTerms}>
            {isLoading ? "Creating account..." : "Create Account"}
          </Button>
        </div>
        
        <div className="text-center text-sm">
          Already have an account?{" "}
          <Link 
            to="/auth/login" 
            className="text-primary hover:underline"
          >
            Sign in
          </Link>
        </div>
      </form>

      <LegalDocumentModal
        isOpen={isOpen}
        onClose={closeDocument}
        documentType={documentType}
      />
    </div>
  );
}
